import { Injectable, inject, signal } from '@angular/core';
import { BannerNotificationApi } from '../api/banner-notification.api';
import { BannerNotification, MaintenanceMode } from '../models/banner-notification.model';

@Injectable({ providedIn: 'root' })
export class BannerNotificationStore {
  private bannerNotificationApi = inject(BannerNotificationApi);
  private _loading = signal(false);
  private _maintenanceMode = signal<MaintenanceMode[]>([]);
  private _bannerNotifications = signal<BannerNotification[]>([]);
  private _dismissedBanners = signal<Set<string>>(new Set());

  readonly loading = this._loading.asReadonly();
  readonly maintenanceMode = this._maintenanceMode.asReadonly();
  readonly bannerNotifications = this._bannerNotifications.asReadonly();
  readonly dismissedBanners = this._dismissedBanners.asReadonly();

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  setMaintenanceMode(maintenanceMode: MaintenanceMode[]): void {
    this._maintenanceMode.set(maintenanceMode);
  }

  setBannerNotifications(notifications: BannerNotification[]): void {
    this._bannerNotifications.set(notifications);
  }

  addDismissedBanner(bannerType: string): void {
    const current = new Set(this._dismissedBanners());
    current.add(bannerType);
    this._dismissedBanners.set(current);
  }

  async fetchMaintenanceMode(): Promise<MaintenanceMode[]> {
    this._loading.set(true);
    try {
      const maintenanceMode = await this.bannerNotificationApi.getMaintenanceMode();
      this._maintenanceMode.set(maintenanceMode);
      return maintenanceMode;
    } finally {
      this._loading.set(false);
    }
  }

  async fetchBannerNotifications(): Promise<BannerNotification[]> {
    this._loading.set(true);
    try {
      const notifications = await this.bannerNotificationApi.getBannerNotifications();
      this._bannerNotifications.set(notifications);
      return notifications;
    } finally {
      this._loading.set(false);
    }
  }

  async dismissBanner(bannerType: string, refetch = false): Promise<boolean> {
    this._loading.set(true);
    try {
      const response = await this.bannerNotificationApi.dismissBannerNotification(bannerType);

      if (response.success) {
        this.addDismissedBanner(bannerType);

        // Optionally refetch to get updated list
        if (refetch) {
          await this.fetchBannerNotifications();
        }
      }

      return response.success;
    } finally {
      this._loading.set(false);
    }
  }

  isBannerDismissed(bannerType: string): boolean {
    return this._dismissedBanners().has(bannerType);
  }

  resetState(): void {
    this._loading.set(false);
    this._maintenanceMode.set([]);
    this._bannerNotifications.set([]);
    this._dismissedBanners.set(new Set());
  }
}
